import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Languages, GraduationCap, Users, BarChart3, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../stores/authStore';
import { useModuleStore } from '../../../stores/moduleStore';
import { api, apiError } from '../../../lib/api';
import { toast } from '../../../stores/notificationStore';
import { setLanguage, getLanguage } from '../../../lib/i18n';

const FEATURES = [
  { icon: GraduationCap, color: '#a78bfa', label: 'Complete academic management' },
  { icon: Users,         color: '#4ade80', label: 'Staff & payroll automation' },
  { icon: BarChart3,     color: '#fb923c', label: 'Real-time analytics & reports' },
  { icon: Shield,        color: '#60a5fa', label: 'Secure, multi-tenant platform' },
];

const STATS = [
  { value: 'Multi-Campus', label: 'Ready' },
  { value: '256-bit',      label: 'Encrypted' },
  { value: '99.9%',        label: 'Uptime' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { refresh: refreshModules } = useModuleStore();
  const { t } = useTranslation(['auth', 'common']);

  const [step, setStep]               = useState<'credentials' | 'otp'>('credentials');
  const [loginMode, setLoginMode]     = useState<'phone' | 'email'>('phone');
  const [form, setForm]               = useState({ phone: '', email: '', password: '', tenant_slug: '', otp: '' });
  const [sessionToken, setSessionToken] = useState('');
  const [showPw, setShowPw]           = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [lang, setLang]               = useState<'en' | 'lg'>(getLanguage);

  function set(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
    setError('');
  }

  function toggleLanguage() {
    const next = lang === 'en' ? 'lg' : 'en';
    setLanguage(next);
    setLang(next);
  }

  async function handleCredentials(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/login', {
        ...(loginMode === 'phone'
          ? { phone: form.phone.trim() }
          : { email: form.email.trim().toLowerCase() }),
        password:    form.password,
        tenant_slug: form.tenant_slug.trim().toLowerCase(),
      });
      const data = res.data.data;
      if (data.requires_otp) {
        setSessionToken(data.session_token ?? '');
        setStep('otp');
        toast('info', t('auth:otp.subtitle', { phone: form.phone }));
        return;
      }
      await completeLogin(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleOtp(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/otp/verify', {
        session_token: sessionToken,
        otp_code:      form.otp.trim(),
      });
      await completeLogin(res.data.data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  async function completeLogin(data: {
    access_token: string;
    user: { id: string; first_name: string; last_name: string; role: string; permissions: string[]; phone: string };
    tenant: { id: string; name: string; slug: string; school_type: string; pricing_tier: string; logo_url?: string };
  }) {
    setAuth(data.user, data.tenant, data.access_token);
    try { await refreshModules(); } catch { /* non-fatal */ }

    let destination = '/dashboard';
    try {
      const ob = await api.get('/onboarding');
      if (!ob.data?.data?.completed) destination = '/onboarding';
    } catch { /* if fetch fails, go to dashboard */ }

    toast('success', `Welcome back, ${data.user.first_name}!`);
    navigate(destination, { replace: true });
  }

  return (
    <div className="login-root">

      {/* ── Left brand panel ── */}
      <div className="login-brand-panel">
        <div className="login-brand-pattern" />
        <div className="login-brand-orb-orange" />
        <div className="login-brand-orb" />
        <div className="login-brand-orb-warm" />

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 52, position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 13,
            background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 700, color: '#fff',
            fontFamily: "'Space Grotesk', sans-serif",
            boxShadow: '0 4px 20px rgba(124,58,237,0.5)',
          }}>U</div>
          <div>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>
              USMS
            </span>
            <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
              by{' '}
              <a href="https://zoelan.com" target="_blank" rel="noopener noreferrer"
                style={{ color: '#F5A623', fontWeight: 700, textDecoration: 'none' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={e => (e.currentTarget.style.color = '#F5A623')}
              >
                zoelan
              </a>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <div style={{ position: 'relative', zIndex: 1, marginBottom: 40 }}>
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 38, fontWeight: 700, color: '#ffffff',
            letterSpacing: '-0.03em', lineHeight: 1.15, marginBottom: 16,
          }}>
            Manage your<br />school smarter
          </h1>
          <p style={{ fontSize: 15, color: 'rgba(196,191,255,0.85)', lineHeight: 1.65, maxWidth: 340 }}>
            Uganda's complete school ERP — academics, finance, HR, and government reporting in one platform.
          </p>
        </div>

        {/* Divider */}
        <div style={{ width: 64, height: 2, background: 'linear-gradient(90deg, #7C3AED 0%, #fb923c 70%, transparent 100%)', marginBottom: 36, borderRadius: 2, position: 'relative', zIndex: 1 }} />

        {/* Feature list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 52, position: 'relative', zIndex: 1 }}>
          {FEATURES.map(({ icon: Icon, color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 9,
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon size={15} color={color} />
              </div>
              <span style={{ fontSize: 13.5, color: 'rgba(220,215,255,0.85)', fontWeight: 400 }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          {STATS.map(({ value, label }, i) => (
            <div key={label} style={{
              flex: 1,
              textAlign: 'center',
              paddingRight: i < STATS.length - 1 ? 0 : 0,
              borderRight:  i < STATS.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
            }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: i === 0 ? '#fb923c' : '#fff' }}>{value}</div>
              <div style={{ fontSize: 11, color: 'rgba(196,191,255,0.7)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="login-right-panel">
        <div className="login-right-orb-orange" />
        <div className="login-right-orb-purple" />

        {/* Language toggle */}
        <button className="login-lang-btn" onClick={toggleLanguage}>
          <Languages size={13} />
          {lang === 'en' ? 'Luganda' : 'English'}
        </button>

        {/* Mobile logo */}
        <div className="login-mobile-logo">
          <div style={{
            width: 50, height: 50, borderRadius: 14,
            background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, fontWeight: 700, color: '#fff',
            fontFamily: "'Space Grotesk', sans-serif",
            boxShadow: '0 4px 20px rgba(83,74,183,0.35)',
            marginBottom: 12,
          }}>U</div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: '#26215C' }}>USMS</div>
          <div style={{ fontSize: 13, color: '#7B78A8', marginTop: 4 }}>Uganda School Management System</div>
        </div>

        {/* Form card */}
        <div className="login-form-card">
          {step === 'credentials' ? (
            <>
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>Sign in to your account</h2>
              </div>

              {/* Login mode tabs */}
              <div style={{
                display: 'flex', gap: 0, marginBottom: 22,
                background: '#F0EFF8', borderRadius: 10, padding: 4,
              }}>
                {(['phone', 'email'] as const).map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setLoginMode(mode)}
                    style={{
                      flex: 1, height: 34, border: 'none', cursor: 'pointer',
                      borderRadius: 7, fontSize: 13, fontWeight: 600,
                      transition: 'all 0.15s',
                      background: loginMode === mode ? '#fff' : 'transparent',
                      color: loginMode === mode ? '#534AB7' : '#7B78A8',
                      boxShadow: loginMode === mode ? '0 1px 4px rgba(83,74,183,0.15)' : 'none',
                    }}
                  >
                    {mode === 'phone' ? 'Phone Number' : 'Email Address'}
                  </button>
                ))}
              </div>

              <form onSubmit={handleCredentials}>
                <div className="field">
                  <label>School Code</label>
                  <input
                    className="input login-input"
                    type="text"
                    value={form.tenant_slug}
                    onChange={e => set('tenant_slug', e.target.value)}
                    placeholder="e.g. st-marys-kisubi"
                    required
                  />
                </div>

                {loginMode === 'phone' ? (
                  <div className="field">
                    <label>{t('auth:login.phone')}</label>
                    <input
                      className="input login-input"
                      type="tel"
                      value={form.phone}
                      onChange={e => set('phone', e.target.value)}
                      placeholder={t('auth:login.phonePlaceholder')}
                      required
                    />
                  </div>
                ) : (
                  <div className="field">
                    <label>Email Address</label>
                    <input
                      className="input login-input"
                      type="email"
                      value={form.email}
                      onChange={e => set('email', e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                )}

                <div className="field">
                  <label>{t('auth:login.password')}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="input login-input"
                      type={showPw ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => set('password', e.target.value)}
                      placeholder="Min. 8 characters"
                      required
                      style={{ paddingRight: 42 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(p => !p)}
                      style={{
                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#7B78A8', display: 'flex',
                      }}
                    >
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div style={{
                    background: '#fee2e2', border: '1px solid rgba(220,38,38,0.2)',
                    borderRadius: 8, padding: '10px 14px',
                    fontSize: 13, color: '#dc2626', marginBottom: 16,
                  }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary login-btn-submit"
                  style={{
                    width: '100%', height: 48, fontSize: 15, fontWeight: 600,
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #534AB7 0%, #f97316 100%)',
                    borderRadius: 12,
                    boxShadow: '0 6px 20px rgba(83,74,183,0.3)',
                    marginTop: 8,
                  }}
                >
                  {loading
                    ? <span style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    : t('auth:login.signIn')}
                </button>
              </form>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Verify your identity</h2>
                <p style={{ fontSize: 14, lineHeight: 1.5 }}>{t('auth:otp.subtitle', { phone: form.phone })}</p>
              </div>

              <form onSubmit={handleOtp}>
                <div className="field">
                  <label>One-Time Code</label>
                  <input
                    className="input login-input mono"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={form.otp}
                    onChange={e => set('otp', e.target.value)}
                    placeholder={t('auth:otp.codePlaceholder')}
                    required
                    data-testid="otp-input"
                    style={{ textAlign: 'center', fontSize: 24, letterSpacing: '0.5em' }}
                  />
                </div>

                {error && (
                  <div style={{
                    background: '#fee2e2', border: '1px solid rgba(220,38,38,0.2)',
                    borderRadius: 8, padding: '10px 14px',
                    fontSize: 13, color: '#dc2626', marginBottom: 16,
                  }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary login-btn-submit"
                  style={{
                    width: '100%', height: 48, fontSize: 15, fontWeight: 600,
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #534AB7 0%, #f97316 100%)',
                    borderRadius: 12,
                    boxShadow: '0 6px 20px rgba(83,74,183,0.3)',
                    marginTop: 8,
                  }}
                >
                  {loading
                    ? <span style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    : t('auth:otp.verify')}
                </button>

                <button
                  type="button"
                  data-testid="otp-back"
                  onClick={() => setStep('credentials')}
                  style={{
                    display: 'block', width: '100%', marginTop: 14,
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: '#7B78A8', textAlign: 'center',
                    transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#4E4B72')}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = '#7B78A8')}
                >
                  ← {t('auth:otp.back')}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="login-footer" style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid rgba(83,74,183,0.15)', textAlign: 'center', fontSize: 14, fontWeight: 500, color: '#4E4B72' }}>
          &copy; {new Date().getFullYear()}{' '}
          <a href="https://zoelan.com" target="_blank" rel="noopener noreferrer"
            style={{ color: '#534AB7', fontWeight: 700, textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#3C3489')}
            onMouseLeave={e => (e.currentTarget.style.color = '#534AB7')}
          >
            zoelan
          </a>
        </p>
      </div>
    </div>
  );
}
